import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, effect, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticatedPageComponent } from 'src/app/components/base.component';
import { MainpageComponent } from 'src/app/components/mainpage/mainpage.component';
import { Shop, ShopItem } from 'src/app/dto';
import { ApplicationService } from 'src/app/services/application.service';
import { ShopService } from 'src/app/services/shop.service';

@Component({
    selector: 'app-shop-editor',
    templateUrl: './shop-editor.component.html',
    styleUrls: ['./shop-editor.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShopEditorComponent extends AuthenticatedPageComponent {
    public static route = 'shop/edit';


    shopId = input<string>();
    // Backed by a signal, not a plain field - both writes below happen inside effect()/subscribe
    // callbacks, never a template-bound handler. equal: () => false because the stock subscribe
    // mutates the held Shop's .Items in place (it's the same cached instance
    // ShopService.activeUserShops holds), so every .set() - even of the same reference - must be
    // treated as a change. The getter keeps every shop.X read/[(ngModel)] site working unchanged.
    private shopState = signal<Shop>(new Shop(null), { equal: () => false });
    // Also reads ShopService.stockRevision so a template reading shop.Items re-renders when an item
    // is spliced out of the held Shop's Items (a purchase, or a "sold out" rejection).
    get shop(): Shop {
        this.shopService.stockRevision();
        return this.shopState();
    }

    constructor(private shopService: ShopService, private modalService: NgbModal, protected coreAppService: ApplicationService, router: Router) {
        super(router, coreAppService.authService, MainpageComponent.route, coreAppService.uiStateService);
        effect((onCleanup) => {
            // ShopService's fetch methods are typed number - route params always arrive as strings.
            const shopId = Number(this.shopId());
            const activeShopsSub = this.shopService.activeUserShops.subscribe(shops => {
                this.shopState.set(shops.filter(s => s.Id == shopId)[0]);
            });
            onCleanup(() => activeShopsSub.unsubscribe());

            // Assignment, not accumulation: getShopItems always returns the shop's full current
            // stock, and this.shop here is the same cached Shop instance activeUserShops holds -
            // pushing onto it would duplicate items on every re-run this effect sees for the same
            // shop (e.g. navigating away and back to it under this app's route reuse strategy).
            const stockSub = this.shopService.getShopItems(shopId).subscribe(stockItems => {
                this.shop.Items = stockItems;
                this.shopState.set(this.shop); // re-notify - same reference, equal: () => false forces it through
            });
            onCleanup(() => stockSub.unsubscribe());
        });
    }

    public updateShop(shop: Shop): void {
        this.shopService.updateShops(shop).subscribe();
    }

    openModal(content: any) {
        this.modalService.open(content);
    }

}
